// pages/AdminForms/AdminForms.js
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './AdminForms.css';
import privateAxios from '../../utils/axios/privateAxios';
import { FaEdit, FaTrash } from 'react-icons/fa';
import ConfirmDialog from '../../components/ConfirmDialog/ConfirmDialog';

const AdminForms = () => {
  const navigate = useNavigate();
  const [forms, setForms] = useState([]);
  const [selectedForms, setSelectedForms] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [searchMode, setSearchMode] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    message: '',
    onConfirm: null,
    loading: false,
  });

  const fetchForms = async (page = 1) => {
    try {
      setLoading(true);
      const res = await privateAxios.get(`/private/admin-forms?page=${page}`);
      setForms(res.data.forms);
      setTotalPages(res.data.totalPages);
      setCurrentPage(page);
    } catch (err) {
      console.error('Error fetching forms', err);
      setForms([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchSearchedForms = async (page = 1) => {
    try {
      setLoading(true);
      const res = await privateAxios.get(`/private/admin-search-forms?query=${searchTerm.trim()}&page=${page}`);
      setForms(res.data.forms);
      setTotalPages(res.data.totalPages);
      setCurrentPage(page);
    } catch (err) {
      console.error('Error searching forms', err);
      setForms([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchForms();
  }, []);

  const handleSearch = () => {
    if (!searchTerm.trim()) {
      setSearchMode(false);
      fetchForms(1);
    } else {
      setSearchMode(true);
      fetchSearchedForms(1);
    }
  };

  const handleCheckbox = (id) => {
    setSelectedForms(prev =>
      prev.includes(id) ? prev.filter(f => f !== id) : [...prev, id]
    );
  };

  const deleteSelectedForms = async () => {
    if (!selectedForms.length) return;
    setConfirmDialog({
      open: true,
      message: `Are you sure you want to delete ${selectedForms.length} selected form(s)?`,
      onConfirm: async () => {
        try {
          setConfirmDialog(prev => ({ ...prev, loading: true }));
          await privateAxios.post('/private/delete-selected-forms', { ids: selectedForms });
          setSelectedForms([]);
          setConfirmDialog({ open: false, message: '', onConfirm: null, loading: false });
          searchMode ? fetchSearchedForms(currentPage) : fetchForms(currentPage);
        } catch (err) {
          console.error('Error deleting forms', err);
          setConfirmDialog(prev => ({ ...prev, loading: false }));
        }
      },
      loading: false
    });
  };

  const handleDeleteSingle = (id, title) => {
    setConfirmDialog({
      open: true,
      message: `Are you sure you want to delete the form titled "${title}"?`,
      onConfirm: async () => {
        try {
          setConfirmDialog(prev => ({ ...prev, loading: true }));
          await privateAxios.delete(`/private/delete-form/${id}`);
          setConfirmDialog({ open: false, message: '', onConfirm: null, loading: false });
          searchMode ? fetchSearchedForms(currentPage) : fetchForms(currentPage);
        } catch (err) {
          console.error('Error deleting form', err);
          setConfirmDialog(prev => ({ ...prev, loading: false }));
        }
      },
      loading: false
    });
  };

  return (
    <div className="admin-forms">
      <div className="admin-forms-header">
        <Link to='/create-form' className='admin-forms-create-form'>Create Form</Link>

        <div className='admin-forms-search-input'>
          <input
            type="text"
            placeholder="Search forms..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <button className='admin-forms-search-btn' onClick={handleSearch}>Search</button>
        </div>

        <button
          className="admin-forms-delete-selected-btn"
          style={{ backgroundColor: selectedForms.length ? '#008751' : '#ccf1e4' }}
          onClick={deleteSelectedForms}
          disabled={!selectedForms.length}
        >
          Delete Selected
        </button>
      </div>

      <div className="admin-forms-list">
        {loading ? (
          <div>Loading...</div>
        ) : forms.length ? (
          forms.map(form => (
            
            <div  className="admin-forms-card" key={form._id}>
              
              <div className='admin-forms-checkbox-title'>
                <input
                className='admin-forms-checkbox'
                  type="checkbox"
                  checked={selectedForms.includes(form._id)}
                  onChange={() => handleCheckbox(form._id)}
                />
                <Link to={`/admin-form/${form._id}`}>
                <div className="admin-forms-info">
                  <h4>{form.title}</h4>
                  <small>Created: {new Date(form.createdAt).toLocaleDateString()}</small>
                </div>
                </Link>
              </div>
              
              <div className="admin-forms-actions">
                <button className='admin-forms-edit-button' onClick={() => navigate(`/admin-edit-form/${form._id}`)}><FaEdit /></button>
                <button className='admin-forms-delete-button' onClick={() => handleDeleteSingle(form._id, form.title)}><FaTrash /></button>
              </div>
            </div>
          ))
        ) : (
          <div>No forms found.</div>
        )}
      </div>

      <div className="admin-forms-pagination-controls">
        <button onClick={() => searchMode ? fetchSearchedForms(currentPage - 1) : fetchForms(currentPage - 1)} disabled={currentPage === 1}>Previous</button>
        <span>Page {currentPage} of {totalPages}</span>
        <button onClick={() => searchMode ? fetchSearchedForms(currentPage + 1) : fetchForms(currentPage + 1)} disabled={currentPage === totalPages}>Next</button>
      </div>

      <div className="admin-forms-back">
        <Link to='/admin-dashboard'>Back to Admin Dashboard</Link>
      </div>

      {confirmDialog.open && (
        <ConfirmDialog
          message={confirmDialog.message}
          onConfirm={confirmDialog.onConfirm}
          onCancel={() => setConfirmDialog({ open: false, message: '', onConfirm: null, loading: false })}
          loading={confirmDialog.loading}
        />
      )}
    </div>
  );
};

export default AdminForms;